//------------------OPTIMIZED REPORTS TAB COMPONENT

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Grid, Typography, Stack } from '@mui/material';
import { handleExportError } from "../../../utils/snackBarUtil";
import { fetchPowerBIReportsKpi, fetchPowerBIReportDatasetDetails, fetchReportGrowthData, fetchReportUsageData, fetchPowerBIExportReportActivityDetails, fetchUserActivityFilterOptions, fetchUserActivityUsersFilter } from '../../../service/service';
import NumberWidget from 'components/Widgets/NumberWidget';
import { CustomizedDataGrid } from '../../customized_data_grid';
import { Column } from 'react-data-grid';
import LoaderComponent from 'components/Loader';
import HeatMapWidget from 'components/Widgets/HeatMapWidget';
import CustomDropdown from 'components/Dropdown';
import ReactECharts from 'echarts-for-react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
import { getPepwiseSectorList } from 'service/service';
import moment from 'moment';

import { LastRefreshDateFormatter, RefreshScheduleFormatter, CapacityNameFormatter, WorkspaceNameFormatter, ReportNameFormatter, DatasetNameFormatter, ViewCountFormatter, SectorFormatter, ReportCreationDateFormatter, OwnerFormatter, DatasetCreationDateFormatter, LastRefreshDurationFormatter, FailureReasonFormatter, LastRefreshStatusFormatter, RefreshTypeFormatter, TableNameFormatter, MQueryFormatter } from './formatters';
import { getSectorsArray, expandSectors, normalizeAndSort, formatValue } from './helper';

interface KPITilesProps {
	filters?: {
		sector?: string | string[];
		filterOption?: string;
		startDate?: string;
		endDate?: string;
		filterList?: null | { [key: string]: any };
		chartTimePeriod?: string;
		lastRefreshDuration?: any;
	};
	timeRange?: string;
	sectorsChange?: string;
	checkedListRef?: React.MutableRefObject<any>;
}

type KpiItem = {
	title: string;
	value: string;
	change: number;
	changeType: 'increase' | 'decrease';
	color: string;
	info: string;
};

function useWindowSize() {
	const [size, setSize] = useState({ width: 0, height: 0 });

	useEffect(() => {
		function handleResize() {
			setSize({ width: window.innerWidth, height: window.innerHeight });
		}

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	return size;
}

const ReportsTab: React.FC<KPITilesProps> = ({ filters, timeRange, sectorsChange, checkedListRef }) => {
	const theme = useTheme();
	const colors = theme.palette;
	console.log('ReportsTab rendered with props:', { filters, timeRange, sectorsChange });

	// State declarations
	const [loading, setLoading] = useState<boolean>(true);
	const [reportGrowthLoading, setReportGrowthLoading] = useState<boolean>(true);
	const [reportUsageLoading, setReportUsageLoading] = useState<boolean>(true);
	const [, setError] = useState<string | null>(null);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0 });
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const [filterInfo, setFilterInfo] = useState<{ [key: string]: any }>({});
	const [searchDetails] = useState([]);
	const [datasetDetails] = useState<Row[]>([]);
	const [chartOption, setChartOption] = useState<any>();
	const [reportChartOption, setReportChartOption] = useState<any[] | undefined>(undefined);
	const [heatmapXLabels, setHeatmapXLabels] = useState<string[] | undefined>(undefined);
	const [heatmapYLabels, setHeatmapYLabels] = useState<string[] | undefined>(undefined);
	const [timePeriod, setTimePeriod] = useState('Monthly');
	const [heatmapPeriod, setHeatmapPeriod] = useState<'Daily' | 'Weekly' | 'Monthly'>('Daily');
	const [allSectors, setAllSectors] = useState<string[]>([]);
	const [sectorsLoaded, setSectorsLoaded] = useState<boolean>(false);
	const [kpiItems, setKpiItems] = useState<KpiItem[]>([
		{
			title: 'Low Usage Reports',
			value: '0',
			change: 0,
			changeType: 'increase',
			color: '#00D4FF',
			info: 'Total number of reports having views between 1 and 10 within the selected date range'
		},
		{
			title: 'Unused Reports',
			value: '0',
			change: 0,
			changeType: 'decrease',
			color: '#FF6B6B',
			info: 'Total number of reports having 0 views within the selected date range'
		},
		{
			title: 'Reports with Similar Datasets',
			value: '0',
			change: 0,
			changeType: 'increase',
			color: '#4ECDC4',
			info: 'The total number of reports that are connected to datasets used by more than one report'
		},
		{
			title: 'Total Dataset Refreshes',
			value: '0',
			change: 0,
			changeType: 'increase',
			color: '#45B7D1',
			info: 'Total number of dataset refreshes within the selected date range'
		},
		{
			title: 'Total Dataset Tables',
			value: '0',
			change: 0,
			changeType: 'increase',
			color: '#96CEB4',
			info: 'The total number of tables contained across all datasets'
		}
	]);
	const [selectedTitle, setSelectedTitle] = useState<string>('');
	const [internalFilters, setInternalFilters] = useState<KPITilesProps['filters']>(filters || {});
	const { width } = useWindowSize();
	let heightDiffMap: number;

	if (width < 768) {
		heightDiffMap = -60;
	} else if (width < 1070) {
		heightDiffMap = 0;
	} else if (width < 1090) {
		heightDiffMap = -100;
	} else if (width < 1190) {
		heightDiffMap = -70;
	} else {
		heightDiffMap = -70;
	}
	const [filterDataOptions, setFilterDataOptions] = useState<any>(null);
	const [searchAfterMap, setSearchAfterMap] = useState(new Map());
	const [activeKPI, setActiveKPI] = useState<string | null>('Low Usage Reports');
	const [kpiTableData, setKpiTableData] = useState<any[]>([]);
	const [kpiTableLoading, setKpiTableLoading] = useState<boolean>(false);
	const [kpiTableTitle, setKpiTableTitle] = useState<string>('Dataset Monitoring');

	// Add ref to track the latest timeRange
	const timeRangeRef = useRef<string | undefined>(timeRange);

	// Update the ref whenever timeRange changes
	useEffect(() => {
		timeRangeRef.current = timeRange;
		console.log('⏰ timeRangeRef updated to:', timeRangeRef.current);
	}, [timeRange]);

	// Add ref to track dependency changes
	interface FetchState {
		page: number;
		pageSize: number;
		sortingInfo: string;
		internalFilters: string;
		timeRange: string | undefined;
		filterInfo: string;
		sector: string | string[] | undefined;
		timePeriod?: string;
		heatmapPeriod?: 'Daily' | 'Weekly' | 'Monthly';
		activeKPI: string | null;
		sectorsChange: string | undefined;
	}

	// Initialize the ref with proper typing
	const lastFetchRef = useRef<FetchState>({
		page: 1,
		pageSize: 100,
		sortingInfo: JSON.stringify({ sortBy: '', sortDir: '' }),
		internalFilters: JSON.stringify({}),
		timeRange: undefined,
		filterInfo: JSON.stringify({}),
		sector: undefined,
		activeKPI: 'Low Usage Reports',
		sectorsChange: undefined
	});

	interface Row {
		datasetName: string;
		capacityName: string;
		workspaceName: string;
		lastRefreshDuration: string;
		lastRefreshStatus: string;
		failureReason: string;
	}

	// Clear any stored column preferences for this table to ensure all columns are visible
	localStorage.removeItem('dataset-monitoring_user_col_pref');

	// ========== OPTIMIZED USEFFECTS ==========

	// Fetch all sectors on mount
	useEffect(() => {
		const fetchAllSectors = async () => {
			try {
				const response = await getPepwiseSectorList();
				if (response?.success && response?.data?.sectors) {
					const sectorNames = response.data.sectors
						.map((sector: any) => sector.Sector_Name)
						.filter((name: string) => name !== 'Unknown');
					setAllSectors(sectorNames);
					setSectorsLoaded(true);
				}
			} catch (error) {
				console.error('Error fetching sectors:', error);
				setSectorsLoaded(true);
			}
		};

		fetchAllSectors();
	}, []);

	// Initialize filter options when component mounts
	// Initialize filter options when component mounts
	useEffect(() => {
		const fetchFilterOptions = async () => {
			try {
				let sectors = await getPepwiseSectorList();
				const usersResponse = await fetchUserActivityUsersFilter('');
				const users = usersResponse?.data || [];
				const filterSectors = sectors?.data?.sectors.map((s: any) => s.Sector_Name) || [];
				const datasetNames = await fetchUserActivityFilterOptions('datasetName', '', 'inactive');
				const capacityNames = await fetchUserActivityFilterOptions('capacityName', '', 'inactive');
				const workspaceNames = await fetchUserActivityFilterOptions('workspaceName', '', 'inactive');
				const lastRefreshDuration = await fetchUserActivityFilterOptions('lastRefreshDuration', '', 'active');
				const reportNames = await fetchUserActivityFilterOptions('reportName', '', 'inactive');

				setFilterDataOptions({
					reportName: reportNames?.data || [],
					sector: filterSectors,
					datasetName: datasetNames?.data || [],
					capacityName: capacityNames?.data || [],
					workspaceName: workspaceNames?.data || [],
					lastRefreshStatus: ['Completed', 'Failed'],
					lastRefreshDuration: lastRefreshDuration?.data || [],
					refreshType: ['Scheduled', 'OnDemand'],
					owner: users
				});
			} catch (error) {
				console.error('Error fetching filter options for Reports tab:', error);
			}
		};

		fetchFilterOptions();
	}, []);

	// Update internal filters when parent filters change
	useEffect(() => {
		if (filters) {
			setInternalFilters(filters);
		}
	}, [filters]);

	// Initial data fetch when sectors load
	useEffect(() => {
		if (sectorsLoaded) {
			console.log('🚀 Initial data fetch');
			fetchKPIData();
			fetchChartData();
			fetchReportChartData();

			// Set default active KPI and fetch its data
			if (!activeKPI) {
				setActiveKPI('Low Usage Reports');
				setSelectedTitle('Low Usage Reports');
				fetchCurrentKPIData(1);
			}
		}
	}, [sectorsLoaded]);

	// ========== MEMOIZED FUNCTIONS ==========

	const fetchKPIData = useCallback(async () => {
		if (!sectorsLoaded) return;
		try {
			setLoading(true);
			setError(null);

			let updatedFilters = { ...filters };

			if (checkedListRef?.current) {
				const selectedSectors = checkedListRef?.current?.length > 0
					? checkedListRef?.current?.join(',')
					: allSectors.join(',');
				updatedFilters = {
					...updatedFilters,
					sector: selectedSectors
				};
			}

			console.log('Fetching KPI data with filters:', updatedFilters);

			const response = await fetchPowerBIReportsKpi(updatedFilters);
			if (response.data?.success) {
				console.log('KPI data received:', response.data.data);
				fetchKpiItems(response.data.data);
			} else {
				throw new Error('Failed to fetch KPI data');
			}
		} catch (err: any) {
			console.error('Error fetching KPI data:', err);
			setError(err?.response?.data?.message || err.message || 'Failed to fetch KPI data');
		} finally {
			setLoading(false);
		}
	}, [sectorsLoaded, filters, checkedListRef, allSectors]);

	const fetchChartData = useCallback(async () => {
		if (!sectorsLoaded) return;
		setReportGrowthLoading(true);
		try {
			let updatedFilters = { ...filters };

			if (checkedListRef?.current) {
				const selectedSectors = checkedListRef?.current?.length > 0
					? checkedListRef?.current?.join(',')
					: allSectors.join(',');
				updatedFilters = {
					...updatedFilters,
					sector: selectedSectors
				};
			}

			updatedFilters = {
				...updatedFilters,
				chartTimePeriod: timePeriod
			};

			console.log('Fetching report growth data with filters:', updatedFilters);

			const response = await fetchReportGrowthData(updatedFilters);
			if (response.data?.success) {
				const rawData = response.data.data.chartData;

				const transformedData = rawData.map((item: { period: string;[key: string]: string | number }) => {
					const { period, ...sectorData } = item;
					return {
						[period]: sectorData
					};
				});

				console.log('Report growth chart data transformed:', transformedData);
				setChartOption(transformedData);
			}
		} catch (error) {
			console.error('Error fetching report growth data:', error);
		} finally {
			setReportGrowthLoading(false);
		}
	}, [sectorsLoaded, filters, checkedListRef, allSectors, timePeriod]);

	const fetchReportChartData = useCallback(async () => {
		if (!sectorsLoaded) return;
		setReportUsageLoading(true);
		try {
			let updatedFilters = { ...filters };

			if (checkedListRef?.current) {
				const selectedSectors = checkedListRef?.current?.length > 0
					? checkedListRef?.current?.join(',')
					: allSectors.join(',');
				updatedFilters = {
					...updatedFilters,
					sector: selectedSectors
				};
			}

			const requestFilters = {
				...updatedFilters,
				chartTimePeriod: heatmapPeriod
			};

			console.log('🔍 Fetching report usage heatmap data with filters:', requestFilters);

			const response = await fetchReportUsageData(requestFilters);

			if (response.data?.success) {
				const heatmapData = response.data.data.heatmap;

				if (heatmapData?.points && Array.isArray(heatmapData.points)) {
					setHeatmapXLabels(heatmapData?.xLabels || []);
					setHeatmapYLabels(heatmapData?.yLabels || []);
					setReportChartOption(heatmapData?.points || []);
				} else {
					console.error('❌ Invalid heatmap points data:', heatmapData?.points);
					setHeatmapXLabels([]);
					setHeatmapYLabels([]);
					setReportChartOption([]);
				}
			} else {
				console.error('❌ API response not successful:', response.data);
				setHeatmapXLabels([]);
				setHeatmapYLabels([]);
				setReportChartOption([]);
			}
		} catch (error: any) {
			console.error('💥 Error fetching report usage heatmap data:', error);
			setHeatmapXLabels([]);
			setHeatmapYLabels([]);
			setReportChartOption([]);
		} finally {
			setReportUsageLoading(false);
		}
	}, [sectorsLoaded, filters, checkedListRef, allSectors, heatmapPeriod]);

	// ========== DATA FETCHING FUNCTIONS ==========

	const fetchLowUsageReports = async (targetPage: number = page): Promise<any[]> => {
		console.log('fetchlowusagepage--', targetPage);
		console.log('fetchtimerange from ref--', timeRangeRef.current);
		const selectedSectors = getProcessedSectors(checkedListRef, allSectors);
		const updatedFilters = {
			...internalFilters,
			sector: selectedSectors
		};

		if (internalFilters?.sector !== selectedSectors) {
			setInternalFilters(updatedFilters);
		}

		const payload: any = {
			...updatedFilters,
			page: targetPage,
			pageSize,
			kpiType: 'lowUsageReports',
			sortBy: sortingInfo.sortBy || 'viewCount',
			orderBy: sortingInfo.sortDir || 'asc',
			filterOption: timeRangeRef.current,
		};

		console.log('📤 Final payload with timeRange:', payload.filterOption);

		const searchAfterKey = `lowUsageReports-${targetPage - 1}`;
		const searchAfter = searchAfterMap.get(searchAfterKey);

		if (searchAfter) {
			payload.searchAfter = searchAfter;
		}

		if (internalFilters?.filterList) {
			payload.filters = internalFilters.filterList;
		}

		const response = await fetchPowerBIReportDatasetDetails(payload);

		if (response?.data?.success) {
			const total = response.data.data.totalDatasets || 0;
			setPaginationData({ totalElements: total, totalPages: Math.ceil(total / pageSize) });

			if (response.data.data.searchAfter) {
				const currentPageKey = `lowUsageReports-${targetPage}`;
				setSearchAfterMap(prev => new Map(prev).set(currentPageKey, response.data.data.searchAfter));
			}

			return response.data.data.reports || [];
		}
		return [];
	};

	const fetchUnusedReports = async (targetPage: number = page): Promise<any[]> => {
		console.log('fetchUnusedReports with timeRange:', timeRangeRef.current);

		const payload: any = {
			...internalFilters,
			page: targetPage,
			pageSize,
			kpiType: 'unusedReports',
			sortBy: sortingInfo.sortBy || 'lastViewed',
			orderBy: sortingInfo.sortDir || 'desc',
			filterOption: timeRangeRef.current,
		};

		if (internalFilters?.filterList) {
			payload.filters = internalFilters.filterList;
		}

		const response = await fetchPowerBIReportDatasetDetails(payload);

		if (response?.data?.success) {
			const total = response.data.data.totalDatasets || 0;
			setPaginationData({ totalElements: total, totalPages: Math.ceil(total / pageSize) });
			return response.data.data.reports || [];
		}
		return [];
	};

	const fetchSimilarDatasetReports = async (targetPage: number = page): Promise<any[]> => {
		console.log('fetchSimilarDatasetReports with timeRange:', timeRangeRef.current);

		const payload: any = {
			...internalFilters,
			page: targetPage,
			pageSize,
			kpiType: 'similarDatasetReports',
			sortBy: sortingInfo.sortBy || 'reportName',
			orderBy: sortingInfo.sortDir || 'asc',
			filterOption: timeRangeRef.current,
		};

		if (internalFilters?.filterList) {
			payload.filters = internalFilters.filterList;
		}

		const response = await fetchPowerBIReportDatasetDetails(payload);

		if (response?.data?.success) {
			const total = response.data.data.totalDatasets || 0;
			setPaginationData({ totalElements: total, totalPages: Math.ceil(total / pageSize) });
			return response.data.data.reports || [];
		}
		return [];
	};

	const getProcessedSectors = (
		checkedListRef: React.MutableRefObject<any> | undefined,
		allSectors: string[]
	): string => {

		const normalizeSector = (sector: string) =>
			sector.toLowerCase() === 'europe' ? 'Europe' : sector;

		const rawSectors = checkedListRef?.current?.length > 0
			? checkedListRef?.current
			: allSectors;

		const expanded = rawSectors.flatMap((sector: any) =>
			sector === 'AMESA/APAC' ? ['AMESA', 'APAC'] : [sector]
		);

		let finalList = expanded.map(normalizeSector);

		const hasAMESA = finalList.includes('AMESA');
		const hasAPAC = finalList.includes('APAC');

		if (hasAMESA && hasAPAC) {
			finalList = finalList.filter((s: string) => s !== 'AMESA' && s !== 'APAC');
			finalList.push('AMESA/APAC');
		}

		return finalList.join(',');
	};


	const fetchDatasetRefreshes = async (targetPage = page): Promise<any[]> => {
		console.log('fetchDatasetRefreshes with timeRange:', timeRangeRef.current);

		const selectedSectors = getProcessedSectors(checkedListRef, allSectors);

		const updatedFilters = {
			...internalFilters,
			sector: selectedSectors
		};

		if (internalFilters?.sector !== selectedSectors) {
			setInternalFilters(updatedFilters);
		}

		const payload: any = {
			...updatedFilters,
			page: targetPage,
			pageSize,
			kpiType: 'datasetRefreshes',
			sortBy: sortingInfo.sortBy || 'startTime',
			orderBy: sortingInfo.sortDir || 'desc',
			filterOption: timeRangeRef.current,
		};

		if (updatedFilters?.filterList) {
			payload.filters = updatedFilters.filterList;
		}
		const searchAfterKey = `datasetRefreshes-${targetPage - 1}`;
		const searchAfter = searchAfterMap.get(searchAfterKey);

		if (searchAfter) {
			payload.searchAfter = searchAfter;
		}

		const response: any = await fetchPowerBIReportDatasetDetails(payload);
		if (response?.data?.success) {
			const total = response.data.data.totalDatasets || 0;
			setPaginationData({ totalElements: total, totalPages: Math.ceil(total / pageSize) });

			if (response.data.data.searchAfter) {
				const currentPageKey = `datasetRefreshes-${targetPage}`;
				setSearchAfterMap(prev => new Map(prev).set(currentPageKey, response.data.data.searchAfter));
			}

			return response.data.data.datasets || [];
		}
		return [];
	};

	const fetchDatasetTables = async (targetPage: number = page): Promise<any[]> => {
		console.log('fetchDatasetTables with timeRange:', timeRangeRef.current, targetPage);

		const payload: any = {
			...internalFilters,
			page: targetPage,
			pageSize,
			kpiType: 'datasetTables',
			sortBy: sortingInfo.sortBy || 'datasetName',
			orderBy: sortingInfo.sortDir || 'asc',
			filterOption: timeRangeRef.current,
		};

		if (internalFilters?.filterList) {
			payload.filters = internalFilters.filterList;
		}
		const searchAfterKey = `datasetTables-${targetPage - 1}`;
		let searchAfter = searchAfterMap.get(searchAfterKey);
		if (targetPage - page > 1)
			searchAfter = null;

		console.log('SearchAfter lookup:', {
			targetPage,
			searchAfter,
			searchAfterKey,
			searchAfterExists: !!searchAfter,
			searchAfterMap: Array.from(searchAfterMap.entries())
		});

		if (searchAfter) {
			payload.searchAfter = searchAfter;
		}

		const response = await fetchPowerBIReportDatasetDetails(payload);

		if (response?.data?.success) {
			const total = response.data.data.totalDatasets || 0;
			setPaginationData({ totalElements: total, totalPages: Math.ceil(total / pageSize) });
			if (response.data.data.searchAfter) {
				const currentPageKey = `datasetTables-${targetPage}`;
				setSearchAfterMap(prev => new Map(prev).set(currentPageKey, response.data.data.searchAfter));

				console.log('Stored searchAfter for page:', targetPage, response.data.data.searchAfter);
			}
			return response.data.data.tables || [];
		}
		return [];
	};

	// Handle KPI card clicks to filter table
	const handleKPICardClick = useCallback(async (cardTitle: string, shouldSetActive: boolean = true, targetPage: number = page) => {
		console.log('KPI Card clicked:', cardTitle, 'page:', targetPage, 'timeRange:', timeRangeRef.current);

		if (shouldSetActive && activeKPI !== cardTitle) {
			setPage(1);
			setSearchAfterMap(new Map());
			targetPage = 1;
		}

		if (shouldSetActive) {
			setSelectedTitle(cardTitle);
			setActiveKPI(cardTitle);
		}

		setKpiTableLoading(true);

		try {
			let tableData: any[] = [];
			let title = cardTitle;

			switch (cardTitle) {
				case 'Low Usage Reports':
					tableData = await fetchLowUsageReports(targetPage);
					title = 'Details – Low Usage Reports';
					break;
				case 'Unused Reports':
					tableData = await fetchUnusedReports(targetPage);
					title = 'Details – Unused Reports';
					break;
				case 'Reports with Similar Datasets':
					tableData = await fetchSimilarDatasetReports(targetPage);
					title = 'Details – Reports with Similar Datasets';
					break;
				case 'Total Dataset Refreshes':
					tableData = await fetchDatasetRefreshes();
					title = 'Details – Total Dataset Refreshes';
					break;
				case 'Total Dataset Tables':
					tableData = await fetchDatasetTables(targetPage);
					title = 'Details – Total Dataset Tables';
					break;
				default:
					tableData = [];
			}

			setKpiTableData(tableData);
			setKpiTableTitle(title);
		} catch (error) {
			console.error(`Error fetching ${cardTitle} data:`, error);
			setKpiTableData([]);
		} finally {
			setKpiTableLoading(false);
		}
	}, [page, activeKPI, fetchLowUsageReports, fetchUnusedReports, fetchSimilarDatasetReports, fetchDatasetRefreshes, fetchDatasetTables]);

	const fetchCurrentKPIData = useCallback(async (currentPage: number) => {
		if (activeKPI) {
			await handleKPICardClick(activeKPI, true, currentPage);
		}
	}, [activeKPI, handleKPICardClick]);

	// ========== COLUMN DEFINITIONS ==========

	const getLowUsageReportsColumns = useCallback((): Array<Column<any>> => [
		{
			key: 'capacityName',
			name: 'Capacity Name',
			minWidth: 180,
			formatter: CapacityNameFormatter,
		},
		{
			key: 'workspaceName',
			name: 'Workspace Name',
			minWidth: 200,
			formatter: WorkspaceNameFormatter,
		},
		{
			key: 'reportName',
			name: 'Report Name',
			minWidth: 220,
			formatter: ReportNameFormatter,
		},
		{
			key: 'datasetName',
			name: 'Dataset Name',
			minWidth: 200,
			formatter: DatasetNameFormatter,
		},
		{
			key: 'viewCount',
			name: 'View Count',
			minWidth: 120,
			formatter: ViewCountFormatter,
		},
		{
			key: 'sector',
			name: 'Sector',
			minWidth: 120,
			formatter: SectorFormatter,
		}
	], []);

	const getUnusedReportsColumns = useCallback((): Array<Column<any>> => [...getLowUsageReportsColumns(),
	{
		key: 'reportCreationDate',
		name: 'Report Creation Date',
		minWidth: 180,
		formatter: ReportCreationDateFormatter,
	},
	{
		key: 'owner',
		name: 'Owner',
		minWidth: 180,
		formatter: OwnerFormatter,
	}
	], []);

	const getSimilarDatasetColumns = useCallback((): Array<Column<any>> => [
		{
			key: 'reportName',
			name: 'Report Name',
			minWidth: 220,
			formatter: ReportNameFormatter,
		},
		{
			key: 'datasetName',
			name: 'Dataset Name',
			minWidth: 200,
			formatter: DatasetNameFormatter,
		},
		{
			key: 'workspaceName',
			name: 'Workspace Name',
			minWidth: 200,
			formatter: WorkspaceNameFormatter,
		},
		{
			key: 'capacityName',
			name: 'Capacity Name',
			minWidth: 180,
			formatter: CapacityNameFormatter,
		},
		{
			key: 'sector',
			name: 'Sector',
			minWidth: 120,
			formatter: SectorFormatter,
		},
		{
			key: 'reportCreationDate',
			name: 'Report Creation Date',
			minWidth: 180,
			formatter: ReportCreationDateFormatter,
		},
		{
			key: 'datasetCreationDate',
			name: 'Dataset Creation Date',
			minWidth: 180,
			formatter: DatasetCreationDateFormatter,
		},
		{
			key: 'owner',
			name: 'Owner',
			minWidth: 180,
			formatter: OwnerFormatter,
		}
	], []);

	const getDatasetRefreshesColumns = useCallback((): Array<Column<any>> => [
		{
			key: 'datasetName',
			name: 'Dataset Name',
			minWidth: 200,
			formatter: DatasetNameFormatter,
		},
		{
			key: 'refreshSchedule',
			name: 'Refresh Schedule',
			width: 250,
			sortable: false,
			formatter: RefreshScheduleFormatter,
		}
		,
		{
			key: 'lastRefreshDuration',
			name: 'Last Refresh Duration',
			minWidth: 180,
			formatter: LastRefreshDurationFormatter,
		},
		{
			key: 'lastRefreshTime',
			name: 'Last Refresh Start (UTC)',
			minWidth: 180,
			formatter: LastRefreshDateFormatter,
		},
		{
			key: 'capacityName',
			name: 'Capacity Name',
			minWidth: 180,
			formatter: CapacityNameFormatter,
		},
		{
			key: 'workspaceName',
			name: 'Workspace Name',
			minWidth: 200,
			formatter: WorkspaceNameFormatter,
		},
		{
			key: 'failureReason',
			name: 'Failure Reason',
			width: 200,
			sortable: false,
			formatter: FailureReasonFormatter,
		},
		{
			key: 'refreshType',
			name: 'Refresh Type',
			minWidth: 150,
			formatter: RefreshTypeFormatter,
		},
		{
			key: 'lastRefreshStatus',
			name: 'Last Refresh Status',
			minWidth: 180,
			formatter: LastRefreshStatusFormatter,
		}
	], []);

	const getDatasetTablesColumns = useCallback((): Array<Column<any>> => [
		{
			key: 'workspaceName',
			name: 'Workspace Name',
			minWidth: 200,
			formatter: WorkspaceNameFormatter,
		},
		{
			key: 'capacityName',
			name: 'Capacity Name',
			minWidth: 180,
			formatter: CapacityNameFormatter,
		},
		{
			key: 'datasetName',
			name: 'Dataset Name',
			minWidth: 200,
			formatter: DatasetNameFormatter,
		},
		{
			key: 'tableName',
			name: 'Table Name',
			minWidth: 200,
			sortable: false,
			formatter: TableNameFormatter,
		},
		{
			key: 'mQuery',
			name: 'MQuery',
			width: 250,
			sortable: false,
			formatter: MQueryFormatter,
		}
	], []);

	const getColumns = useCallback((): Array<Column<any>> => {
		switch (activeKPI) {
			case 'Low Usage Reports':
				return getLowUsageReportsColumns();
			case 'Unused Reports':
				return getUnusedReportsColumns();
			case 'Reports with Similar Datasets':
				return getSimilarDatasetColumns();
			case 'Total Dataset Refreshes':
				return getDatasetRefreshesColumns();
			case 'Total Dataset Tables':
				return getDatasetTablesColumns();
			default:
				return [
					{
						key: 'datasetName',
						name: 'Dataset',
						minWidth: 230,
						formatter: DatasetNameFormatter,
					},
					{
						key: 'capacityName',
						name: 'Capacity',
						minWidth: 190,
						formatter: CapacityNameFormatter,
					},
					{
						key: 'workspaceName',
						name: 'Workspace',
						minWidth: 150,
						formatter: WorkspaceNameFormatter,
					},
					{
						key: 'lastRefreshDuration',
						name: 'Last Refresh Duration',
						minWidth: 230,
						formatter: LastRefreshDurationFormatter,
					},
					{
						key: 'lastRefreshStatus',
						name: 'Last Refresh Status',
						minWidth: 230,
						formatter: LastRefreshStatusFormatter,
					},
					{
						key: 'failureReason',
						name: 'Failure Reason',
						minWidth: 200,
						formatter: FailureReasonFormatter,
					}
				];
		}
	}, [activeKPI, getLowUsageReportsColumns, getUnusedReportsColumns, getSimilarDatasetColumns, getDatasetRefreshesColumns, getDatasetTablesColumns]);

	// ========== EVENT HANDLERS ==========

	const handleSearch = async (searchDetails: any, columnKey: any) => {
		if (searchDetails?.length >= 3) {
			try {
				let response;

				switch (columnKey) {
					case 'datasetName':
					case 'capacityName':
					case 'workspaceName':
					case 'lastRefreshStatus':
						response = await fetchUserActivityFilterOptions(columnKey, searchDetails, 'inactive');
						break;
					case 'lastRefreshDuration':
						response = await fetchUserActivityFilterOptions(columnKey, searchDetails, 'active');
						break;
					case 'owner':
						response = await fetchUserActivityUsersFilter(searchDetails);
						break;
					default:
						return;
				}

				const options = response?.data || [];

				setFilterDataOptions((prev: any) => ({
					...prev,
					[columnKey]: options
				}));

			} catch (error) {
				console.error('Error performing search:', error);
			}
		} else if (searchDetails?.length === 0) {
			if (['datasetName', 'capacityName', 'workspaceName', 'lastRefreshStatus'].includes(columnKey)) {
				const response = await fetchUserActivityFilterOptions(columnKey, '', 'inactive');
				const initialOptions = response?.data || [];
				setFilterDataOptions((prev: any) => ({
					...prev,
					[columnKey]: initialOptions
				}));
			}
		}
	};

	useEffect(() => {
		if (!sectorsLoaded) return;

		const rawSectors = checkedListRef?.current.length > 0
			? checkedListRef?.current
			: allSectors;
		const expandedSectors = rawSectors.flatMap((sector: any) =>
			sector === 'AMESA/APAC' ? ['AMESA', 'APAC'] : [sector]
		);

		const selectedSectors = expandedSectors.join(',');

		const currentState: FetchState = {
			page,
			pageSize,
			sortingInfo: JSON.stringify(sortingInfo),
			internalFilters: JSON.stringify(internalFilters),
			timeRange: timeRange || undefined,
			filterInfo: JSON.stringify(filterInfo),
			sector: selectedSectors,
			activeKPI,
			sectorsChange
		};

		const lastState = lastFetchRef.current;

		// Debug log to see what's happening with timeRange
		console.log('🔄 TimeRange check:', {
			currentTimeRange: timeRange,
			lastTimeRange: lastState.timeRange,
			areDifferent: timeRange !== lastState.timeRange
		});

		// Check if anything actually changed
		const hasChanges = (Object.keys(currentState) as (keyof FetchState)[]).some((key) =>
			currentState[key] !== lastState[key]
		);

		if (!hasChanges) return;

		const timeoutId = setTimeout(() => {
			console.log('🔄 Refetching data due to dependencies change');

			// Handle sector changes
			if (sectorsChange && sectorsChange.startsWith('sectors-') && sectorsChange !== lastState.sectorsChange) {
				console.log('🔄 Refetching data due to sector change');
				setPage(1);
				setSearchAfterMap(new Map());
				fetchCurrentKPIData(1);
				fetchKPIData();
				fetchChartData();
				fetchReportChartData();
				lastFetchRef.current = currentState;
				return;
			}

			// Helper function to expand "AMESA/APAC" and normalize for comparison
			const normalizeSectorForComparison = (sectorString: string | string[] | undefined): string => {
				if (!sectorString) return '';

				const sectorsArray = getSectorsArray(sectorString);
				const expandedSectors = expandSectors(sectorsArray);
				const normalizedSectors = normalizeAndSort(expandedSectors);

				return normalizedSectors;
			};

			// Get current processed sectors
			const currentProcessedSectors = getProcessedSectors(checkedListRef, allSectors);

			// Then in your reset conditions:
			const resetConditions = [
				timeRange !== lastState.timeRange,
				JSON.stringify(sortingInfo) !== lastState.sortingInfo,
				JSON.stringify(filterInfo) !== lastState.filterInfo,
				normalizeSectorForComparison(currentProcessedSectors) !==
				normalizeSectorForComparison(lastState.sector),
			];

			if (resetConditions.some(Boolean)) {
				console.log('🔄 Resetting to page 1 due to filter/sort changes');
				setPage(1);
				setSearchAfterMap(new Map());
				fetchCurrentKPIData(1);

				// Only fetch KPI and charts when relevant filters change
				const shouldFetchKPIAndCharts = [
					timeRange !== lastState.timeRange,
					normalizeSectorForComparison(currentProcessedSectors) !==
					normalizeSectorForComparison(lastState.sector),
				].some(Boolean);

				if (shouldFetchKPIAndCharts) {
					console.log('🔄 Fetching KPI and chart data due to filter changes');
					fetchKPIData();
					fetchChartData();
					fetchReportChartData();
				}
			} else if (page !== lastState.page || pageSize !== lastState.pageSize) {
				console.log('🔄 Fetching current KPI data for pagination');
				fetchCurrentKPIData(page);
			}

			// Update ref with current state
			lastFetchRef.current = currentState;
		}, 150);

		return () => clearTimeout(timeoutId);
	}, [
		page,
		pageSize,
		sortingInfo,
		internalFilters,
		timeRange,
		filterInfo,
		filters?.sector,
		sectorsChange,
		activeKPI,
		sectorsLoaded,
		fetchCurrentKPIData,
		fetchKPIData,
		fetchChartData,
		fetchReportChartData
	]);

	useEffect(() => {
		if (!sectorsLoaded) return;

		fetchChartData();
	}, [timePeriod, sectorsLoaded]); // Only watch timePeriod

	// Separate useEffect for Report Usage Heatmap ONLY  
	useEffect(() => {
		if (!sectorsLoaded) return;

		fetchReportChartData();
	}, [heatmapPeriod, sectorsLoaded]); // Only watch heatmapPeriod


	console.log('Time period : ', timePeriod)

	const handleSorting = (sorting: any) => {
		console.log("sorting details", sorting);

		const fieldMap: Record<string, string> = {
			datasetName: "datasetName",
			workspaceName: "workspaceName",
			capacityName: "capacityName",
			lastRefereshStatus: "lastRefreshStatus",
			lastRefereshDuration: "lastRefreshDuration",
			startTime: "startTime",
			endTime: "endTime",
			timestamp: "timestamp",
		};

		const backendField = fieldMap[sorting.sortBy] || sorting.sortBy;

		setSortingInfo({ sortBy: backendField, sortDir: sorting.sortDir });
		setPage(1);
	};



	const handleFilter = (filter: { [key: string]: string[] | string | any[] }) => {
		const filterListMap: { [key: string]: string | string[] } = {};
		const clearedColumns: string[] = []; // Track which columns were cleared

		Object.keys(filter).forEach((key) => {
			const value = filter[key];

			// Check if this filter is being cleared (empty array or null)
			if (value == null || (Array.isArray(value) && value.length === 0)) {
				clearedColumns.push(key);
				return;
			}

			if (key === 'reportCreationDate' && Array.isArray(value) && value.length === 2) {
				const [startMoment, endMoment] = value;

				let utcStart = startMoment;
				let utcEnd = endMoment;

				if (moment.isMoment(startMoment)) {
					utcStart = moment.utc([
						startMoment.year(),
						startMoment.month(),
						startMoment.date(),
						startMoment.hours(),
						startMoment.minutes(),
						startMoment.seconds()
					]).toISOString();
				}

				if (moment.isMoment(endMoment)) {
					utcEnd = moment.utc([
						endMoment.year(),
						endMoment.month(),
						endMoment.date(),
						endMoment.hours(),
						endMoment.minutes(),
						endMoment.seconds()
					]).toISOString();
				}

				filterListMap[key] = [utcStart, utcEnd];
			} else {
				// Normal filter (non-date)
				filterListMap[key] = value;
			}
		});
		// setFilterInfo({}); // Clear all filter info
		if (Object.keys(filterListMap).length === 0) {
			setFilterInfo({});
			setInternalFilters({})
		}
		else {
			// 🧩 Update the filters state
			setFilterInfo(prev => ({ ...prev, ...filterListMap }));

			setInternalFilters(prev => ({
				...prev,
				filterList: Object.keys(filterListMap).length > 0 ? filterListMap : null
			}));
		}

		setPage(1);

		if (clearedColumns.length > 0) {
			clearedColumns.forEach(async (columnKey) => {
				try {
					let response;

					switch (columnKey) {
						case 'workspaceName':
						case 'datasetName':
						case 'capacityName':
						case 'lastRefreshStatus':
							response = await fetchUserActivityFilterOptions(columnKey, '', 'inactive');
							break;
						case 'lastRefreshDuration':
							response = await fetchUserActivityFilterOptions(columnKey, '', 'active');
							break;
						case 'owner':
							response = await fetchUserActivityUsersFilter('');
							break;
						default:
							return;
					}

					const options = response?.data || [];

					setFilterDataOptions((prev: any) => ({
						...prev,
						[columnKey]: options
					}));

				} catch (error) {
					console.error(`Error refreshing filter options for ${columnKey}:`, error);
				}
			});
		}
	};


	const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
		setPage(value);
	};

	const handlepageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
		const newSize = Number(event.target.value);
		setPageSize(newSize);
		setPage(1);
	};


	const exportGetReportActivityDetails = async () => {
		let tableType = activeKPI ? `kpi-${activeKPI.toLowerCase().replaceAll(/\s+/g, '-')}` : "dataset-monitoring"

		console.info('Exporting report activity details for KPI:', tableType)
		let exportColumns = getDefaultColumnNames(activeKPI);
		let formatType = "EXCEL"
		let tableName = ""
		let sortBy, orderBy

		let kpiType = "";
		if (activeKPI === 'Low Usage Reports') {
			sortBy = sortingInfo.sortBy || 'viewCount'
			orderBy = sortingInfo.sortDir || 'asc'
			kpiType = 'lowUsageReports'
		}
		else if (activeKPI === 'Unused Reports') {
			sortBy = sortingInfo.sortBy || 'lastViewed'
			orderBy = sortingInfo.sortDir || 'desc'
			kpiType = 'unusedReports'
		}
		else if (activeKPI === 'Reports with Similar Datasets') {
			sortBy = sortingInfo.sortBy || 'reportName'
			orderBy = sortingInfo.sortDir || 'asc'
			kpiType = 'similarDatasetReports'
		}
		else if (activeKPI === 'Total Dataset Tables') {
			sortBy = sortingInfo.sortBy || 'datasetName'
			orderBy = sortingInfo.sortDir || 'asc'
			kpiType = 'datasetTables'
		}
		else if (activeKPI === 'Total Dataset Refreshes') {
			sortBy = sortingInfo.sortBy || 'lastRefreshDuration'
			orderBy = sortingInfo.sortDir || 'desc'
			kpiType = 'datasetRefreshes'
		}
		const cleanFilters = Object.fromEntries(
			Object.entries(filterInfo).filter(([_, v]) => Array.isArray(v) ? v.length > 0 : v != null)
		);
		const requestBody = {
			...internalFilters,
			filterOption: timeRangeRef.current,
			sortBy: sortBy,
			orderBy: orderBy,
			filters: cleanFilters,
			kpiType: kpiType,
			exportColumns: exportColumns,
			formatType: formatType,
			tableName: tableName,
		};

		try {
			let res = await fetchPowerBIExportReportActivityDetails(requestBody);
			console.log(res)
		}
		catch (error) {
			console.error(error);
			handleExportError(error);
		}
	};

	// ========== UTILITY FUNCTIONS ==========

	const formatNumber = (num: number): string => {
		if (num >= 1000000) {
			return `${(num / 1000000).toFixed(1)}M`;
		} else if (num >= 1000) {
			return `${(num / 1000).toFixed(1)}K`;
		}
		return num.toString();
	};

	const fetchKpiItems = async (kpiData: any) => {
		const kpiItems = [
			{
				title: 'Low Usage Reports',
				value: formatNumber(kpiData?.lowUsageReports?.value || 0),
				change: kpiData?.lowUsageReports?.change || 0,
				changeType: kpiData?.lowUsageReports?.changeType || 'increase',
				color: '#00D4FF',
				info: 'Total number of reports having views between 1 and 10 within the selected date range'
			},
			{
				title: 'Unused Reports',
				value: formatNumber(kpiData?.unusedReports?.value || 0),
				change: kpiData?.unusedReports?.change || 0,
				changeType: kpiData?.unusedReports?.changeType || 'decrease',
				color: '#FF6B6B',
				info: 'Total number of reports having 0 views within the selected date range'
			},
			{
				title: 'Reports with Similar Datasets',
				value: formatNumber(kpiData?.reportsWithSimilarDatasets?.value || 0),
				change: kpiData?.reportsWithSimilarDatasets?.change || 0,
				changeType: kpiData?.reportsWithSimilarDatasets?.changeType || 'increase',
				color: '#4ECDC4',
				info: 'The total number of reports that are connected to datasets used by more than one report'
			},
			{
				title: 'Total Dataset Refreshes',
				value: formatNumber(kpiData?.totalDatasetRefreshes?.value || 0),
				change: kpiData?.totalDatasetRefreshes?.change || 0,
				changeType: kpiData?.totalDatasetRefreshes?.changeType || 'increase',
				color: '#45B7D1',
				info: 'Total number of dataset refreshes within the selected date range'
			},
			{
				title: 'Total Dataset Tables',
				value: formatNumber(kpiData?.totalDatasetTables?.value || 0),
				change: kpiData?.totalDatasetTables?.change || 0,
				changeType: kpiData?.totalDatasetTables?.changeType || 'increase',
				color: '#96CEB4',
				info: 'The total number of tables contained across all datasets'
			}
		];
		setKpiItems(kpiItems)
	};

	const generateSectorColors = (sectors: string[]): { [key: string]: string } => {
		const predefinedColors: { [key: string]: string } = {
			'PBNA': '#3498db',
			'PFNA': '#1abc9c',
			'AMESA': '#f1c40f',
			'CGF': '#e67e22',
			'LATAM': '#e74c3c',
			'EUROPE': '#9b59b6',
			'APAC': '#95a5a6',
			'Corporate': '#34495e'
		};

		const colorPalette = [
			'#3498db', '#1abc9c', '#f1c40f', '#e67e22', '#e74c3c', '#9b59b6',
			'#95a5a6', '#34495e', '#2ecc71', '#f39c12', '#d35400', '#c0392b',
			'#8e44ad', '#2c3e50', '#16a085', '#27ae60', '#f1c40f', '#d68910',
			'#cb4335', '#7d3c98', '#5d6d7e', '#148f77', '#229954', '#dc7633'
		];

		const colorMap: { [key: string]: string } = {};

		sectors.forEach((sector, index) => {
			if (predefinedColors[sector]) {
				colorMap[sector] = predefinedColors[sector];
			} else if (index < colorPalette.length) {
				colorMap[sector] = colorPalette[index];
			} else {
				const hue = (index * 137.5) % 360;
				const saturation = 65 + (index % 3) * 10;
				const lightness = 45 + (index % 4) * 5;
				colorMap[sector] = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
			}
		});

		return colorMap;
	};


	const getDefaultColumnNames = useCallback((activeKPI: string | null): string[] => {
		switch (activeKPI) {
			case 'Low Usage Reports':
				return ['Capacity Name', 'Workspace Name', 'Report Name', 'Dataset Name', 'View Count', 'Sector', 'Last Viewed'];
			case 'Unused Reports':
				return ['Capacity Name', 'Workspace Name', 'Report Name', 'Dataset Name', 'View Count', 'Sector', 'Last Viewed', 'Report Creation Date', 'Owner'];
			case 'Reports with Similar Datasets':
				return ['Report Name', 'Dataset Name', 'Workspace Name', 'Capacity Name', 'Sector', 'Report Creation Date', 'Dataset Creation Date', 'Owner'];
			case 'Total Dataset Refreshes':
				return ['Dataset Name', 'Refresh Schedule', 'Last Refresh Duration', 'Capacity Name', 'Workspace Name', 'Failure Reason', 'Refresh Type', 'Last Refresh Status', 'Last Refresh Start (UTC)'];
			case 'Total Dataset Tables':
				return ['Workspace Name', 'Capacity Name', 'Dataset Name', 'Table Name', 'MQuery'];
			default:
				return ['Dataset', 'Capacity', 'Workspace', 'Last Refresh Duration', 'Last Refresh Status', 'Failure Reason'];
		}
	}, []);

	// ========== CHART RENDER FUNCTIONS ==========

	const renderReportGrowthChart = () => {
		const rawData = chartOption || [];

		const allSectors = new Set<string>();
		rawData.forEach((item: any) => {
			const periodData = Object.values(item)[0] as any;
			if (periodData && typeof periodData === 'object') {
				Object.keys(periodData).forEach(sector => {
					allSectors.add(sector);
				});
			}
		});

		const sectorsArray = Array.from(allSectors).sort((a, b) => a.localeCompare(b));
		const sectorColorMap = generateSectorColors(sectorsArray);

		const seriesData = sectorsArray.map((sector: string) => ({
			name: sector,
			dataKey: sector,
			color: sectorColorMap[sector]
		}));

		// Helper function to calculate total from period data
		const calculatePeriodTotal = (periodData: any): number => {
			return Object.keys(periodData).reduce((total, key) => {
				return total + (periodData[key] || 0);
			}, 0);
		};

		// Helper function to format label
		const formatLabelTotal = (params: any): string => {
			const periodData = Object.values(rawData[params.dataIndex])[0] as any;
			const total = calculatePeriodTotal(periodData);
			return formatValue(total);
		};

		const customChartOption: any = {
			backgroundColor: colors.widget?.background?.chart,
			tooltip: {
				trigger: 'axis',
				backgroundColor: colors.widget?.chart?.tooltipBg,
				borderColor: colors.widget?.chart?.legendTextColor,
				borderWidth: 1,
				textStyle: {
					color: colors.widget?.chart?.tooltipText,
					fontSize: 12
				},
				axisPointer: {
					type: 'shadow',
					shadowStyle: {
						color: colors.widget?.chart?.emptyCircleFill
					}
				},
				formatter: function (params: any) {
					let tooltipContent = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].name}</div>`;
					let totalReports = 0;

					params.forEach((param: any) => {
						if (param.value > 0) {
							totalReports += param.value;
							const formattedValue = formatValue(param.value);
							tooltipContent += `
								<div style="display: flex; align-items: center; margin-bottom: 4px;">
									<span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 8px; border-radius: 2px;"></span>
									<span style="margin-right: 8px;">${param.seriesName}:</span>
									<span style="font-weight: bold;">${formattedValue}</span>
								</div>
							`;
						}
					});

					const formattedTotal = formatValue(totalReports);

					tooltipContent += `<div style="border-top: 1px solid ${colors.widget?.chart?.legendTextColor}; padding-top: 6px; margin-top: 6px;">Total Reports: ${formattedTotal}</div>`;

					return tooltipContent;
				}
			},
			legend: {
				show: true,
				type: 'scroll',
				orient: 'horizontal',
				left: '20px',
				bottom: '20px',
				itemWidth: 14,
				itemHeight: 14,
				itemGap: 15,
				textStyle: {
					color: colors.widget?.chart?.legendTextColor,
					fontSize: 11
				},
				pageIconColor: colors.widget?.chart?.legendActiveUpDownIconColor,
				pageIconInactiveColor: colors.widget?.chart?.legendInactiveUpDownIconColor,
				pageTextStyle: {
					color: colors.widget?.chart?.legendTextColor,
					fontSize: 10
				},
				data: seriesData.map(s => s.name)
			},
			grid: {
				left: '3%',
				right: '4%',
				bottom: '25%',
				top: '10%',
				containLabel: true
			},
			xAxis: {
				type: 'category' as const,
				data: rawData.map((item: any) => Object.keys(item)[0]),
				axisLabel: {
					color: colors.widget?.chart?.labelColorSecondary,
					fontSize: 11,
					interval: 0,
					rotate: 0
				},
				axisLine: {
					show: true,
					lineStyle: {
						color: colors.widget?.chart?.legendTextColor
					}
				},
				axisTick: {
					show: false
				},
				splitLine: {
					show: false
				}
			},
			yAxis: {
				type: 'value' as const,
				name: 'Reports',
				nameLocation: 'middle',
				nameGap: 60,
				nameTextStyle: {
					color: colors.widget?.chart?.labelColorSecondary,
					fontSize: 12
				},
				axisLabel: {
					color: colors.widget?.chart?.labelColorSecondary,
					fontSize: 11,
					formatter: formatValue
				},
				axisLine: {
					show: false
				},
				axisTick: {
					show: false
				},
				splitLine: {
					show: true,
					lineStyle: {
						type: 'dashed',
						color: colors.widget?.chart?.emptyCircleFill,
						width: 1
					}
				}
			},
			series: seriesData.map((series: any, index: number) => ({
				name: series.name,
				type: 'bar' as const,
				stack: 'total',
				data: rawData.map((item: any) => {
					const periodData = Object.values(item)[0] as any;
					return periodData[series.dataKey] || 0;
				}),
				itemStyle: {
					color: series.color
				},
				emphasis: {
					focus: 'series'
				},
				label: index === seriesData.length - 1 ? {
					show: true,
					position: 'top',
					formatter: formatLabelTotal,
					color: colors.widget?.chart?.labelColorSecondary,
					fontSize: 11,
					fontWeight: 'bold'
				} : undefined
			}))
		};

		return (
			<Stack
				minHeight="100px"
				p={2}
				bgcolor={colors.widget?.background?.chart}
				fontFamily="Montserrat"
				borderRadius="5px"
				flexGrow={1}
				height='calc(100% - 74px)'
				maxHeight='320px'
			>
				<Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" pb={1}>
					<Typography variant="widgetTitle" sx={{ color: colors.widget?.text?.title }}>Report Growth</Typography>
					<Box display='flex' gap={1}>
						<CustomDropdown
							handleChange={(e: { target: { value: string } }) => setTimePeriod(e.target.value)}
							value={timePeriod}
							options={[
								{ label: 'Monthly', value: 'Monthly' },
								{ label: 'Weekly', value: 'Weekly' }
							]}
							dropdownIcon={KeyboardArrowDownIcon}
							selectedTextColor={colors.DropdownV2?.dropdownIconDefault}
							textColor={colors.DropdownV2?.menuItemText}
							iconColor={colors.DropdownV2?.dropdownIconDefault}
							bgColor={colors.DropdownV2?.menuItemBackground}
							borderColor="transparent"
							hoverBgColor={colors.DropdownV2?.menuItemHover}
							hoverTextColor={colors.DropdownV2?.menuItemHoverText}
							selectedBgColor={colors.DropdownV2?.menuItemSelectedBg}
							boxProps={{
								sx: {
									backgroundColor: 'transparent !important',
									'& .MuiOutlinedInput-root': {
										backgroundColor: 'transparent !important'
									},
									'& .MuiSelect-select': {
										fontFamily: 'Montserrat',
										fontSize: '14px',
										fontWeight: 500,
										padding: '0 26px 0 0 !important',
										minHeight: '28px !important',
										display: 'flex',
										alignItems: 'center'
									},
									'& .MuiOutlinedInput-notchedOutline': {
										border: 'none !important'
									},
									'& .MuiSelect-icon': {
										color: `${colors.DropdownV2?.dropdownIconDefault} !important`,
										right: 0
									}
								}
							}}
						/>
					</Box>
				</Stack>
				<Stack height="280px" flexGrow={1} justifyContent="center">
					{reportGrowthLoading ? (
						<LoaderComponent />
					) : (
						<ReactECharts
							option={customChartOption}
							style={{ width: '100%', height: '100%' }}
							opts={{ renderer: 'canvas', width: 'auto', height: 'auto' }}
							notMerge={true}
							lazyUpdate={true}
						/>
					)}
				</Stack>
			</Stack>
		);
	};

	// ========== RENDER ==========

	return (
		<Box sx={{ mb: 3 }}>
			<Grid container spacing={1} sx={{ mb: 3 }}>
				<Grid item xs={12} md={6} sm={12} lg={6}>
					<Grid container spacing={1}>
						{kpiItems.slice(0).map((item, index) => (
							<Grid item xs={12} sm={6} key={`top-${item.title}`}>
								<Box sx={{ position: 'relative' }}>
									<NumberWidget
										title={item.title}
										number={item.value}
										change={item.change}
										titleKey={item.title}
										changeType={item.changeType}
										isLoading={loading}
										showChangePercentage={true}
										tooltipLabel=""
										hasTooltip={true}
										bgColor={theme.palette?.widget?.background?.card}
										color={theme.palette?.widget?.text?.main}
										valueFontWeight={300}
										valueFontSize={{ xs: '24px', sm: '32px', md: '42px' }}
										sameDecimalSize={true}
										selectedTitle={selectedTitle}
										containerProps={{
											minHeight: '140px',
											sx: {
												flexDirection: 'column',
												alignItems: 'flex-start',
												cursor: 'pointer',
												'&:hover': {
													boxShadow: '0 4px 12px rgba(45, 46, 46, 0.3)',
													transform: 'translateY(-2px)',
													transition: 'all 0.2s ease-in-out'
												}
											},
											onClick: () => { void handleKPICardClick(item.title, true, 1) }
										}}
									/>
								</Box>
							</Grid>
						))}
					</Grid>
				</Grid>
				<Grid item xs={12} md={6} sm={12} lg={6}>
					<Grid container spacing={1} sx={{ mb: 0 }}>
						<Grid item xs={12} sm={12} sx={{ height: '222px' }}>
							<HeatMapWidget
								title="Reports Usage Heatmap"
								isLoading={reportUsageLoading}
								data={reportChartOption}
								heightDifferencePercent={heightDiffMap}
								legentText={''}
								xLabels={heatmapXLabels}
								yLabels={heatmapYLabels}
								timePeriod={heatmapPeriod}
							>
								<CustomDropdown
									handleChange={(e: { target: { value: string } }) => setHeatmapPeriod(e.target.value as any)}
									value={heatmapPeriod}
									options={[
										{ label: 'Daily', value: 'Daily' },
										{ label: 'Weekly', value: 'Weekly' },
										{ label: 'Monthly', value: 'Monthly' },
									]}
									dropdownIcon={KeyboardArrowDownIcon}
									selectedTextColor={colors.DropdownV2?.dropdownIconDefault}
									textColor={colors.DropdownV2?.menuItemText}
									iconColor={colors.DropdownV2?.dropdownIconDefault}
									bgColor={colors.DropdownV2?.menuItemBackground}
									borderColor="transparent"
									hoverBgColor={colors.DropdownV2?.menuItemHover}
									hoverTextColor={colors.DropdownV2?.menuItemHoverText}
									selectedBgColor={colors.DropdownV2?.menuItemSelectedBg}
									boxProps={{
										sx: {
											backgroundColor: 'transparent !important',
											'& .MuiOutlinedInput-root': {
												backgroundColor: 'transparent !important'
											},
											'& .MuiSelect-select': {
												fontFamily: 'Montserrat',
												fontSize: '14px',
												fontWeight: 500,
												padding: '0 26px 0 0 !important',
												minHeight: '28px !important',
												display: 'flex',
												alignItems: 'center'
											},
											'& .MuiOutlinedInput-notchedOutline': {
												border: 'none !important'
											},
											'& .MuiSelect-icon': {
												color: `${colors.DropdownV2?.dropdownIconDefault} !important`,
												right: 0
											}
										}
									}}
								/>
							</HeatMapWidget>
						</Grid>
						<Grid item xs={12} sm={12}>
							{renderReportGrowthChart()}
						</Grid>
					</Grid>
				</Grid>
				<Grid container spacing={1} sx={{ mb: 3 }}>
					<Grid item xs={12} sm={12}>
						{kpiTableLoading ? (
							<Stack justifyContent="center" alignItems="center" minHeight="100px">
								<LoaderComponent />
							</Stack>
						) : (
							<CustomizedDataGrid
								title={kpiTableTitle}
								tableColumns={getColumns()}
								tableData={activeKPI ? kpiTableData : datasetDetails}
								tableType={activeKPI ? `kpi-${activeKPI.toLowerCase().replaceAll(/\s+/g, '-')}` : "dataset-monitoring"}
								defaultColumnNames={getDefaultColumnNames(activeKPI)}
								onSorting={handleSorting}
								currSorting={sortingInfo}
								onFilterSearch={handleSearch}
								filterDataOptions={filterDataOptions}
								onFilterCheck={handleFilter}
								currFilter={filterInfo}
								searchDataValues={searchDetails}
								page={page}
								pageSize={pageSize}
								paginationData={paginationData}
								listLength={activeKPI ? kpiTableData.length : datasetDetails.length}
								handlepageSizeChange={handlepageSizeChange}
								handlePageChange={handlePageChange}
								isDateRangeFilter={false}
								isDisabled={false}
								isFetching={kpiTableLoading}
								showExport={true}
								fileName={activeKPI ? `${activeKPI.toLowerCase().replaceAll(/\s+/g, '-')}-report` : "dataset-activity-report"}
								exportTableData={() => exportGetReportActivityDetails()}
							/>
						)}
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};

export default ReportsTab;